// Integration point for search quality assessment.
// Replace the console.log bodies with calls to your analytics / logging service
// without changing the call sites.

/**
 * Report a comparison between the displayed provider's results and a shadow
 * provider's results for the same query. Called by the SearchOrchestrator
 * whenever a shadow provider returns results.
 *
 * @param {Object} report
 * @param {string} report.query                 - The search query
 * @param {string} report.displayedProvider     - ID of the provider whose results are shown
 * @param {Object} report.displayedResults      - Full results shape from the displayed provider
 * @param {string} report.shadowProvider        - ID of the shadow provider
 * @param {Object} report.shadowResults         - Full results shape from the shadow provider
 */
export function reportSearchComparison({
  query,
  displayedProvider,
  displayedResults,
  shadowProvider,
  shadowResults,
}) {
  // eslint-disable-next-line no-console
  console.log('[searchReporter] comparison', {
    query,
    displayedProvider,
    displayedResults,
    shadowProvider,
    shadowResults,
  })
}

/**
 * Report a single provider's results (e.g. for logging purposes without a
 * shadow comparison). Also used by additive providers to record their output.
 *
 * @param {Object} report
 * @param {string} report.provider  - Provider ID
 * @param {string} report.query     - The search query
 * @param {Object} report.results   - Full results shape
 */
export function reportSearchResults({ provider, query, results }) {
  // eslint-disable-next-line no-console
  console.log('[searchReporter] results', { provider, query, results })
}

/* eslint-disable no-console */

// Pull a representative preview string off any hit shape (Algolia or local).
function previewOf(hit) {
  if (!hit) return ''
  return hit.name || hit.label || hit.title || hit.body || hit.url || ''
}

// Build an algolia-side row summary keyed by index name. Each row carries the
// `indexName` separately from the human-readable `source` so downstream
// comparison code can match algolia rows to local rows without parsing
// strings.
function summariseAlgoliaResults(displayedProvider, displayedResults) {
  const indexResults = (displayedResults && displayedResults.indexResults) || {}
  return Object.keys(indexResults).map((indexName) => {
    const result = indexResults[indexName] || {}
    const hits = Array.isArray(result.hits) ? result.hits : []
    return {
      source: `${displayedProvider || 'algolia'} / ${indexName}`,
      indexName,
      nbHits: typeof result.nbHits === 'number' ? result.nbHits : hits.length,
      returned: hits.length,
      hits,
    }
  })
}

// Build a local-side row summary keyed by additive provider id.
//
// Providers that emit Algolia-shaped per-index results (currently
// `local-content`, `minisearch-content`) are expanded into one row per index —
// e.g. `local / local-content / repos` — so the side-by-side table aligns
// row-by-row with the Algolia rows. Providers without `indexResults`
// (navigation, external-help) emit a single flat row with `indexName: null`.
function summariseAdditiveResults(additiveResults) {
  const rows = []
  ;(Array.isArray(additiveResults) ? additiveResults : []).forEach((entry) => {
    if (entry && entry.indexResults && typeof entry.indexResults === 'object') {
      Object.keys(entry.indexResults).forEach((indexName) => {
        const result = entry.indexResults[indexName] || {}
        const hits = Array.isArray(result.hits) ? result.hits : []
        rows.push({
          source: `local / ${entry.providerId} / ${indexName}`,
          providerId: entry.providerId,
          indexName,
          nbHits: typeof result.nbHits === 'number' ? result.nbHits : hits.length,
          returned: hits.length,
          hits,
        })
      })
      return
    }
    const hits = Array.isArray(entry.hits) ? entry.hits : []
    rows.push({
      source: `local / ${entry.providerId}`,
      providerId: entry.providerId,
      indexName: null,
      nbHits: hits.length,
      returned: hits.length,
      hits,
    })
  })
  return rows
}

// Compare each local row that targets a comparable Algolia index by
// `objectID`. Rows whose index has no Algolia counterpart (navigation,
// external-help, etc.) are returned untouched but tagged comparable=false so
// the caller can skip the diff visualisation for them.
function compareToAlgolia(algoliaRows, localRows) {
  const algoliaByIndex = {}
  algoliaRows.forEach((row) => { algoliaByIndex[row.indexName] = row })

  return localRows.map((row) => {
    const algoliaCounterpart = row.indexName ? algoliaByIndex[row.indexName] : null
    if (!algoliaCounterpart) {
      return {
        ...row,
        comparable: false,
        overlap: 0,
        localOnly: [],
        algoliaOnly: [],
      }
    }
    const algoliaIds = new Set(algoliaCounterpart.hits.map((h) => h.objectID))
    const localIds = new Set(row.hits.map((h) => h.objectID))
    const localOnly = row.hits.filter((h) => !algoliaIds.has(h.objectID))
    const algoliaOnly = algoliaCounterpart.hits.filter((h) => !localIds.has(h.objectID))
    const overlap = row.hits.length - localOnly.length
    return {
      ...row,
      comparable: true,
      overlap,
      localOnly,
      algoliaOnly,
    }
  })
}

// Build a console.table-friendly row from any hit. Includes per-engine score
// and matched-key summary for local hits, since this metadata is the main
// reason an evaluator inspects the table.
function tableRowForHit(hit, isLocal) {
  const base = { objectID: hit.objectID, preview: previewOf(hit) }
  if (!isLocal) {
    // Surface Algolia's query-match-quality criteria (and the userScore
    // tie-break) so the side-by-side table shows the same ranking factors the
    // results page sorts on, next to the Fuse/BM25 scores. Guard for
    // _rankingInfo being absent (only present when getRankingInfo is requested).
    const ri = hit._rankingInfo
    if (ri && typeof ri === 'object') {
      base.nbTypos = ri.nbTypos
      base.words = ri.words
      base.proximityDistance = ri.proximityDistance
      base.nbExactWords = ri.nbExactWords
      base.userScore = ri.userScore
    }
    return base
  }
  if (hit.__localEngine === 'minisearch') {
    base.engine = 'minisearch'
    base.score = typeof hit.__minisearchScore === 'number' ?
      Number(hit.__minisearchScore.toFixed(4)) :
      hit.__minisearchScore
    const matchKeys = (hit.__minisearchMatch && typeof hit.__minisearchMatch === 'object') ?
      Object.keys(hit.__minisearchMatch) :
      []
    base.matchedTerms = matchKeys.join(', ')
  } else {
    base.engine = 'fuse'
    base.score = typeof hit.__localScore === 'number' ?
      Number(hit.__localScore.toFixed(4)) :
      hit.__localScore
    base.matchedKeys = Array.isArray(hit.__localMatches) ?
      hit.__localMatches.map((m) => m.key).join(', ') :
      ''
  }
  return base
}

// Pretty-print one row's sample hits as a console.table.
function tableSamples(label, rows) {
  rows.forEach((row) => {
    if (row.hits.length === 0) return
    const isLocal = row.source.startsWith('local /')
    const samples = row.hits.slice(0, 5).map((h) => tableRowForHit(h, isLocal))
    console.groupCollapsed(`${label} samples — ${row.source} (${row.returned} hit${row.returned === 1 ? '' : 's'})`)
    console.table(samples)
    if (isLocal) {
      row.hits.forEach((h) => {
        if (h.__localMatches && h.__localMatches.length > 0) {
          console.log(`  ${h.objectID}  fuse matches:`, h.__localMatches)
        }
        if (h.__minisearchMatch && Object.keys(h.__minisearchMatch).length > 0) {
          console.log(`  ${h.objectID}  minisearch match:`, h.__minisearchMatch, 'terms:', h.__minisearchTerms)
        }
      })
    }
    console.groupEnd()
  })
}

// Render the side-by-side comparison table + per-row diff groups. One row per
// comparable local index so it lines up with the per-index Algolia rows.
function reportComparison(comparedRows) {
  const comparableRows = comparedRows.filter((r) => r.comparable)
  if (comparableRows.length === 0) return

  const counts = comparableRows.map((r) => ({
    source: r.source,
    algoliaReturned: r.hits.length - r.localOnly.length + r.algoliaOnly.length,
    localReturned: r.hits.length,
    overlap: r.overlap,
    localOnly: r.localOnly.length,
    algoliaOnly: r.algoliaOnly.length,
  }))

  console.groupCollapsed('Algolia ↔ Local overlap (per index)')
  console.table(counts)

  // Excess: local hit that Algolia did not return. Common when fuzz tolerance
  // is too loose; the printed identification block lets evaluators decide
  // whether each excess is a useful recall win or a false positive.
  comparableRows.forEach((r) => {
    if (r.localOnly.length === 0) return
    console.groupCollapsed(`Excess (local-only) — ${r.source} · ${r.localOnly.length} hit${r.localOnly.length === 1 ? '' : 's'}`)
    console.table(r.localOnly.map((h) => tableRowForHit(h, true)))
    console.groupEnd()
  })

  // Missing: Algolia hit that local search failed to surface. Typically a
  // false negative caused by tokenisation / typo-tolerance gaps.
  comparableRows.forEach((r) => {
    if (r.algoliaOnly.length === 0) return
    console.groupCollapsed(`Missing (algolia-only) — ${r.source} · ${r.algoliaOnly.length} hit${r.algoliaOnly.length === 1 ? '' : 's'}`)
    console.table(r.algoliaOnly.map((h) => tableRowForHit(h, false)))
    console.groupEnd()
  })

  console.groupEnd()
}

/**
 * Side-by-side comparison of the displayed (Algolia) provider's results vs
 * every additive (local) provider for the current query. Designed for dev /
 * staging quality evaluation while local search runs alongside Algolia.
 *
 * Renders four things in the console:
 *   1. A counts table (nbHits + returned per source) covering every Algolia
 *      and local sub-row.
 *   2. A per-index overlap table: returned count for each side, intersection
 *      size, plus how many results are local-only (excess) and how many are
 *      algolia-only (missing).
 *   3. Per-source identification groups for excess and missing hits — only
 *      printed when non-empty — listing objectID / preview / score / matched
 *      keys so evaluators can categorise each delta as a recall win, false
 *      positive, or false negative.
 *   4. A per-source sample console.table showing the top-5 hits with full
 *      Fuse `matches` / MiniSearch `match` metadata so evaluators can see
 *      exactly which key + token fuzzy-matched the query.
 *
 * @param {Object} report
 * @param {string} report.query
 * @param {string} report.displayedProvider
 * @param {Object} report.displayedResults
 * @param {Array}  report.additiveResults    Array<{ providerId, hits, indexResults? }>
 */
export function reportLocalVsAlgolia({
  query,
  displayedProvider,
  displayedResults,
  additiveResults,
}) {
  if (!query) return

  const algoliaRows = summariseAlgoliaResults(displayedProvider, displayedResults)
  const localRows = summariseAdditiveResults(additiveResults)

  if (algoliaRows.length === 0 && localRows.length === 0) return

  const comparedLocalRows = compareToAlgolia(algoliaRows, localRows)

  const counts = [...algoliaRows, ...localRows].map(({ source, nbHits, returned }) => ({
    source, nbHits, returned,
  }))

  console.groupCollapsed(`[searchReporter] "${query}" — algolia vs local`)
  console.log('Query:', query)
  console.table(counts)
  reportComparison(comparedLocalRows)
  tableSamples('Algolia', algoliaRows)
  tableSamples('Local', localRows)
  console.log('Raw payload:', {
    query, displayedProvider, displayedResults, additiveResults,
  })
  console.groupEnd()
}

/* eslint-enable no-console */
